/**
 * GlobalSearchDialog + HeaderSearchTrigger - the C2 header search.
 *
 * WHAT C2 ASKS FOR (build plan PART 2, C2, Valerio):
 *   "ONE search entry in the top header, to the LEFT of the KI-Assistent button: a bigger
 *    control = Lupe icon + an input box with sample placeholders ('Suche Kunden, Policen,
 *    UID, E-Mail, Domain, Tasks... Ctrl K'). Click opens a CENTERED POPUP over a slightly
 *    blurred current page: type at the top, keyword+semantic results below, click a result to
 *    open that exact page. Include a Schnellnavigation section (fast jump to Dashboard /
 *    Kunden / Policen / Renewals / Rechnungen / Aufgaben / Posteingang / Schaden).
 *    Follow the current Brokly design; do NOT copy Attio or the mockups 1:1.
 *    REMOVE the existing sidebar search entirely."
 *
 * Everything in that paragraph is here except the design copying, which is deliberately not
 * done: this reuses the shell's own tokens (bg-card, border, muted-foreground) so it looks
 * like the rest of Brokly rather than like the reference screenshots.
 *
 * DEBOUNCING is the existing `useSearch` hook's, not a second policy invented here. Each
 * query runs a hybrid search - four keyword legs, a task leg, a domain leg AND an embedding
 * call to apps/ai for the semantic leg - so firing per character would be a request storm
 * against a service that costs real money per embed.
 *
 * [Source: apps/web/src/features/search/SearchBar.tsx] (the api hook + hit shape this replaces)
 * [Source: apps/web/src/features/mandate/MandateWizardDialog.tsx] (the blurred-overlay precedent)
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowRight,
  Loader2,
  Search as SearchIcon,
  X,
} from 'lucide-react';
import { useSearch } from './hooks/useSearch';
import { SemanticDegradedNotice } from './SemanticDegradedNotice';

/** Schnellnavigation, exactly the destinations C2 lists. */
const QUICK_LINKS: Array<{ label: string; to: string }> = [
  { label: 'Dashboard', to: '/' },
  { label: 'Kunden', to: '/kunden' },
  { label: 'Policen', to: '/policen' },
  { label: 'Renewals', to: '/renewals' },
  { label: 'Rechnungen', to: '/rechnungen' },
  { label: 'Aufgaben', to: '/aufgaben' },
  { label: 'Posteingang', to: '/posteingang' },
  { label: 'Schaden', to: '/schaden' },
];

/** German label per hit type, so a result row says what it is. */
const TYPE_LABEL: Record<string, string> = {
  kunde: 'Kunde',
  police: 'Police',
  dokument: 'Dokument',
  kommunikation: 'E-Mail',
  aufgabe: 'Aufgabe',
};

/** Plural labels + fixed order for the result-scope filter chips. */
const TYPE_PLURAL: Record<string, string> = {
  kunde: 'Kunden',
  police: 'Policen',
  dokument: 'Dokumente',
  kommunikation: 'E-Mails',
  aufgabe: 'Aufgaben',
};
const SCOPE_ORDER = ['kunde', 'police', 'dokument', 'kommunikation', 'aufgabe'] as const;

/** Scope-chip className — active = lavender/primary, inactive = muted. */
const scopeChip = (active: boolean): string =>
  `rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
    active ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent'
  }`;

export const SEARCH_PLACEHOLDER = 'Suche Kunden, Policen, Dokumente, UID, E-Mail, Domain, Tasks …';

/**
 * The header control. A real input rather than a bare icon, because C2 asks for "a bigger
 * control" carrying the placeholders - the placeholder text is how a broker learns what is
 * searchable. Clicking anywhere on it opens the dialog, which is where the typing happens.
 */
export function HeaderSearchTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Suche öffnen"
      className="flex min-w-0 max-w-md flex-1 items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-accent"
    >
      <SearchIcon className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{SEARCH_PLACEHOLDER}</span>
      <kbd className="ml-auto hidden shrink-0 rounded border px-1 py-0.5 text-[10px] sm:inline">
        Ctrl K
      </kbd>
    </button>
  );
}

export function GlobalSearchDialog({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  // Result-scope filter (issue: the header category buttons were jump-links, not filters).
  // null = «Alle»; a hit-type value narrows the shown results to that category WITHOUT
  // leaving the search. Reset whenever the query changes so a new search always starts «Alle».
  const [scope, setScope] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  // Reuses the SAME hook the old sidebar SearchBar used: it already debounces and already
  // holds the previous results while the next query resolves. Writing a second one would
  // have been two debounce policies for one search.
  const { data, isLoading, error } = useSearch(q);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Escape closes. A search dialog is a lookup, not a form: nothing is lost by closing it,
  // which is the opposite of the mandate wizard where a stray dismissal would throw away a
  // part-finished document.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hits = data?.items ?? [];
  const searched = q.trim().length > 0;
  // Per-type counts drive the scope-filter chips; `shownHits` applies the active scope.
  const typeCounts = hits.reduce<Record<string, number>>((acc, h) => {
    acc[h.type] = (acc[h.type] ?? 0) + 1;
    return acc;
  }, {});
  const shownHits = scope ? hits.filter((h) => h.type === scope) : hits;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-background/60 pt-24 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Suche"
      onClick={onClose}
    >
      <div
        className="mx-4 flex max-h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-card shadow-lg"
        // The panel itself must not close on click, only the backdrop around it.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <SearchIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setScope(null);
            }}
            placeholder={SEARCH_PLACEHOLDER}
            aria-label="Suchbegriff"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          <button
            type="button"
            onClick={onClose}
            aria-label="Suche schliessen"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {/* S4: the search is always keyword + semantic combined. When the semantic leg is
              degraded (embed service down), say so instead of silently returning keyword-only. */}
          {searched && data?.semantic_available === false && (
            <SemanticDegradedNotice variant="dialog" />
          )}

          {error !== null && error !== undefined && (
            <p className="flex items-start gap-2 px-2 py-3 text-sm text-destructive">
              <AlertCircle className="size-4 shrink-0" />
              Die Suche ist fehlgeschlagen.
            </p>
          )}

          {searched && !isLoading && hits.length === 0 && (
            // Honest: a search RAN and found nothing, which is different from "still typing".
            <p className="px-2 py-3 text-sm text-muted-foreground">
              Keine Treffer für «{q.trim()}».
            </p>
          )}

          {hits.length > 0 && (
            <>
              {/* Result-scope filter chips: narrow the results to a category WITHOUT leaving
                  the search (these replace the old jump-links a broker expected to filter). */}
              <div className="mb-1.5 flex flex-wrap gap-1.5 px-2">
                <button type="button" onClick={() => setScope(null)} className={scopeChip(scope === null)}>
                  Alle {hits.length}
                </button>
                {SCOPE_ORDER.filter((t) => typeCounts[t]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setScope(scope === t ? null : t)}
                    className={scopeChip(scope === t)}
                  >
                    {TYPE_PLURAL[t]} {typeCounts[t]}
                  </button>
                ))}
              </div>
              <ul className="flex flex-col">
                {shownHits.map((hit) => (
                <li key={`${hit.type}-${hit.id}`}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-accent"
                    onClick={() => {
                      onClose();
                      void navigate({ to: hit.route });
                    }}
                  >
                    <span className="w-24 shrink-0 text-xs text-muted-foreground">
                      {TYPE_LABEL[hit.type] ?? hit.type}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{hit.title}</span>
                      {hit.subtitle !== null && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {hit.subtitle}
                        </span>
                      )}
                      {/* Wave N1: for a document-content match this is the ts_headline
                          highlight («term» markers — plain text, no HTML) showing WHERE in
                          the document body the query matched. Also surfaces the semantic
                          leg's chunk excerpt, which was previously carried but never shown. */}
                      {hit.snippet !== null && hit.snippet !== '' && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {hit.snippet}
                        </span>
                      )}
                    </span>
                    <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                </li>
              ))}
              </ul>
            </>
          )}

          {/* Schnellnavigation. Always visible: it is the reason to open this dialog when you
              are NOT searching for a record, and hiding it behind an empty query would make
              the shortcut useless for exactly that case. */}
          <div className="mt-2 border-t px-2 pt-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Schnellnavigation</p>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={onClose}
                  className="rounded border px-2 py-1 text-xs hover:bg-accent"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
